import { useMemo } from 'react';
import type { FinancialParams } from '../utils/financialDefaults';
import {
  calculateAllKpis,
  calculateSensitivity,
  calculateSensitivityHeatmap,
  runMonteCarlo,
  type SimulationResults,
  type SystemConfig,
  type FinancialKpis,
  type YearCashflow,
  type CapexBreakdown,
  type SensitivityItem,
  type HeatmapCell,
  type MonteCarloResult,
} from '../utils/financialCalculations';

export interface FinancialModelResult {
  kpis: FinancialKpis;
  cashflows: YearCashflow[];
  capex: CapexBreakdown;
  sensitivity: SensitivityItem[];
  costStructure: { name: string; value: number; color: string }[];
  revenueStructure: { name: string; value: number; color: string }[];
  heatmap: { cells: HeatmapCell[]; xValues: number[]; yValues: number[]; xLabel: string; yLabel: string };
  monteCarlo: MonteCarloResult;
}

export function useFinancialModel(
  sim: SimulationResults,
  sys: SystemConfig,
  fp: FinancialParams,
  degradation: number,
  fiscalEnabled: boolean = true
): FinancialModelResult {
  return useMemo(() => {
    const effectiveFp = fiscalEnabled ? fp : {
      ...fp,
      vpbRate: 0,
      eiaPercentage: 0,
      miaPercentage: 0,
      depreciationYears: fp.projectDuration,
    };

    const { kpis, cashflows, capex } = calculateAllKpis(sim, sys, effectiveFp, degradation);
    const sensitivity = calculateSensitivity(sim, sys, effectiveFp, degradation);
    const heatmap = calculateSensitivityHeatmap(sim, sys, effectiveFp, degradation);
    const monteCarlo = runMonteCarlo(sim, sys, effectiveFp, degradation);

    const y1 = cashflows[1];
    const costStructure = y1 ? [
      { name: 'Grid Import', value: y1.gridCost, color: '#475569' },
      { name: 'Blokstroom', value: y1.blokstroomCost, color: '#6366F1' },
      { name: 'Netbeheerder', value: y1.gridOperatorCost, color: '#7C3AED' },
      { name: 'Brandstof', value: y1.fuelCost, color: '#D97706' },
      { name: 'O&M PV', value: y1.omPv, color: '#0891B2' },
      { name: 'O&M BESS', value: y1.omBess, color: '#3B82F6' },
      { name: 'Verzekering', value: y1.insurance, color: '#94A3B8' },
      { name: 'Huur locatie', value: y1.locationRent, color: '#A855F7' },
      { name: 'Overige kosten', value: y1.customOpex, color: '#78716C' },
    ].filter(d => d.value > 0) : [];

    const revenueStructure = y1 ? [
      { name: 'Laden', value: y1.chargingRevenue, color: '#0891B2' },
      { name: 'Export', value: y1.exportRevenue, color: '#059669' },
      { name: 'SDE++', value: y1.sdeRevenue, color: '#8B5CF6' },
      { name: 'ERE', value: y1.ereRevenue, color: '#F59E0B' },
    ].filter(d => d.value > 0) : [];

    return { kpis, cashflows, capex, sensitivity, costStructure, revenueStructure, heatmap, monteCarlo };
  }, [sim, sys, fp, degradation, fiscalEnabled]);
}
